import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { AlertCircleIcon, CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, ZapIcon, BarChart2Icon, MessageSquareIcon, UsersIcon, LayoutDashboardIcon, FileTextIcon, UserPlusIcon, BriefcaseIcon } from 'lucide-react';
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-597">
      {children}
    </motion.div>;
}
const modules = [{
  num: '01',
  title: 'Motor de Leads',
  description: 'Campaña publicitaria propia con flujo de leads constante.',
  icon: ZapIcon
}, {
  num: '02',
  title: 'CRM White-Label (GHL)',
  description: 'Tu plataforma de gestión, bajo tu marca.',
  icon: LayoutDashboardIcon
}, {
  num: '03',
  title: 'Lead Store',
  description: 'Sistema de distribución de leads a tu equipo.',
  icon: UsersIcon
}, {
  num: '04',
  title: 'WhatsApp Automation',
  description: 'Secuencias de contacto automáticas por WhatsApp.',
  icon: MessageSquareIcon
}, {
  num: '05',
  title: 'Dashboard Operativo',
  description: 'Métricas en tiempo real: CPL, show rate, conversión.',
  icon: BarChart2Icon
}, {
  num: '06',
  title: 'Scripts y Procesos',
  description: 'Documentación de tu proceso de ventas y reclutamiento.',
  icon: FileTextIcon
}, {
  num: '07',
  title: 'Onboarding de Agentes',
  description: 'Sistema para incorporar nuevos agentes con estructura.',
  icon: UserPlusIcon
}, {
  num: '08',
  title: 'Fractional CMO',
  description: 'Acompañamiento estratégico continuo post-instalación.',
  icon: BriefcaseIcon
}];
const roadmap = [{
  weeks: 'Semanas 1–2',
  title: 'Auditoría y diagnóstico',
  description: 'Revisamos tu stack actual, tus procesos y tus métricas.'
}, {
  weeks: 'Semanas 3–4',
  title: 'Configuración del CRM (GHL)',
  description: 'Instalamos pipelines, automatizaciones y usuarios.'
}, {
  weeks: 'Semanas 5–6',
  title: 'Activación de campaña',
  description: 'Lanzamos tu motor de leads con tracking completo.'
}, {
  weeks: 'Semanas 7–8',
  title: 'Lead Store + WhatsApp',
  description: 'Distribución automática y secuencias de contacto.'
}, {
  weeks: 'Semanas 9–10',
  title: 'Dashboards operativos',
  description: 'CPL, contactabilidad, show rate, costo por cita.'
}, {
  weeks: 'Semanas 11–12',
  title: 'Optimización y entrega',
  description: 'Ajustes finales, documentación y transición a Fractional CMO.'
}];
const metrics = [{
  label: 'CPL',
  subtitle: 'Costo por Lead',
  description: 'Cuánto cuesta generar cada lead en tu sistema.'
}, {
  label: 'CTB',
  subtitle: 'Contactabilidad',
  description: 'Qué porcentaje de leads responden al primer contacto.'
}, {
  label: 'SR',
  subtitle: 'Show Rate',
  description: 'Cuántos prospectos llegan a la cita agendada.'
}, {
  label: 'CPC',
  subtitle: 'Costo por Cita / Póliza',
  description: 'El costo real de cada conversión en tu proceso.'
}];
const faqs = [{
  q: '¿Necesito tener GHL ya instalado?',
  a: 'No. Lo instalamos desde cero o migramos tu configuración actual.'
}, {
  q: '¿Cuántos agentes necesito para que esto tenga sentido?',
  a: 'El sistema está diseñado para agencias en crecimiento. Agenda un diagnóstico para evaluar tu caso específico.'
}, {
  q: '¿Qué pasa después de los 90 días?',
  a: 'Puedes continuar con el modelo Fractional CMO o gestionar el sistema de forma independiente.'
}, {
  q: '¿Garantizan resultados?',
  a: 'No garantizamos ingresos ni métricas específicas. Garantizamos la instalación completa del sistema.'
}, {
  q: '¿Pueden integrarse con mi sistema actual?',
  a: 'Sí. Auditamos tu stack actual en la fase de diagnóstico y diseñamos la integración o migración más adecuada.'
}];
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-border-subtle rounded-xl overflow-hidden" data-id="element-598">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface transition-colors" aria-expanded={open} data-id="element-599">
        <span className="font-medium text-white text-sm sm:text-base pr-4" data-id="element-600">
          {q}
        </span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-601">
          <ChevronDownIcon size={18} className="text-[#a0a0a0] flex-shrink-0" data-id="element-602" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: 0.25,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-603">
        <div className="px-5 pb-4 text-sm text-[#a0a0a0] leading-relaxed border-t border-border-subtle pt-3" data-id="element-604">
          {a}
        </div>
      </motion.div>
    </div>;
}
export function DuenosAgenciaPage() {
  return <div className="bg-charcoal" data-id="element-605">
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-606">
        <div className="max-w-4xl mx-auto text-center" data-id="element-607">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-608">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold tracking-wide uppercase mb-6" data-id="element-609">
              Para Dueños de Agencia
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-610">
            No necesitas más campañas.{' '}
            <span className="text-accent" data-id="element-611">Necesitas infraestructura.</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-[#b8b4ae] mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-612">
            En 90 días instalamos tu sistema propio: motor de leads + CRM
            white-label (GHL) + Lead Store + WhatsApp + dashboards.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-613">
            <a href="/contact-us" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent hover:bg-accent-hover text-charcoal font-semibold rounded-xl transition-colors duration-200" data-id="element-614">
              Agendar diagnóstico
              <ArrowRightIcon size={16} data-id="element-615" />
            </a>
            <a href="#roadmap" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border-subtle text-cream hover:border-accent/50 hover:text-accent rounded-xl transition-colors duration-200 font-medium" data-id="element-616">
              Ver el roadmap
            </a>
          </motion.div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border-subtle" data-id="element-617">
        <div className="max-w-5xl mx-auto" data-id="element-618">
          <FadeInSection data-id="element-619">
            <h2 className="text-2xl sm:text-3xl font-bold text-cream text-center mb-12" data-id="element-620">
              El problema no es la falta de leads. Es la falta de sistema.
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-621">
            <FadeInSection delay={0.1} data-id="element-622">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-623">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-624" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-625">
                  Volumen sin consistencia
                </h3>
                <p className="text-sm text-[#b8b4ae] leading-relaxed" data-id="element-626">
                  Campañas que generan volumen pero no consistencia. Los picos y
                  valles hacen imposible planificar.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-627">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-628">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-629" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-630">
                  Dependencia del dueño
                </h3>
                <p className="text-sm text-[#b8b4ae] leading-relaxed" data-id="element-631">
                  Agentes que dependen de ti para cada paso del proceso. Sin
                  sistema, tú eres el cuello de botella.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-632">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-633">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-634" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-635">
                  Sin métricas claras
                </h3>
                <p className="text-sm text-[#b8b4ae] leading-relaxed" data-id="element-636">
                  Sin métricas claras para saber qué está funcionando.
                  Decisiones basadas en intuición, no en datos.
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* 8 MÓDULOS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-637">
        <div className="max-w-6xl mx-auto" data-id="element-638">
          <FadeInSection data-id="element-639">
            <div className="text-center mb-12" data-id="element-640">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-641">
                8 módulos. Un sistema completo.
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-642">
                Cada módulo resuelve una pieza del rompecabezas operativo.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-643">
            {modules.map((mod, i) => {
            const Icon = mod.icon;
            return <FadeInSection key={mod.num} delay={i * 0.06} data-id="element-644">
                  <div className="bg-elevated border border-border-subtle rounded-xl p-5 h-full" data-id="element-645">
                    <div className="flex items-start justify-between mb-4" data-id="element-646">
                      <span className="text-xs font-bold text-accent tracking-widest" data-id="element-647">
                        {mod.num}
                      </span>
                      <Icon size={16} className="text-[#7a7672]" data-id="element-648" />
                    </div>
                    <h3 className="font-semibold text-cream mb-1.5 text-sm" data-id="element-649">
                      {mod.title}
                    </h3>
                    <p className="text-xs text-[#b8b4ae] leading-relaxed" data-id="element-650">
                      {mod.description}
                    </p>
                  </div>
                </FadeInSection>;
          })}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section id="roadmap" className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-651">
        <div className="max-w-4xl mx-auto" data-id="element-652">
          <FadeInSection data-id="element-653">
            <div className="text-center mb-12" data-id="element-654">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-655">
                Roadmap: 90 días para instalar tu infraestructura
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-656">Semana a semana. Sin sorpresas.</p>
            </div>
          </FadeInSection>
          <div className="relative" data-id="element-657">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border-subtle hidden sm:block" data-id="element-658" />
            <div className="space-y-4" data-id="element-659">
              {roadmap.map((item, i) => <FadeInSection key={item.weeks} delay={i * 0.08} data-id="element-660">
                  <div className="flex gap-5 sm:pl-14 relative" data-id="element-661">
                    <div className="hidden sm:flex absolute left-0 w-12 h-12 rounded-full bg-surface border border-border-subtle items-center justify-center flex-shrink-0" data-id="element-662">
                      <span className="text-xs font-bold text-accent" data-id="element-663">
                        {i + 1}
                      </span>
                    </div>
                    <div className="bg-surface border border-border-subtle rounded-xl p-5 flex-1" data-id="element-664">
                      <div className="flex items-start gap-3" data-id="element-665">
                        <div className="sm:hidden w-8 h-8 rounded-full bg-elevated border border-border-subtle flex items-center justify-center flex-shrink-0" data-id="element-666">
                          <span className="text-xs font-bold text-accent" data-id="element-667">
                            {i + 1}
                          </span>
                        </div>
                        <div data-id="element-668">
                          <span className="text-xs text-accent font-semibold uppercase tracking-widest block mb-1" data-id="element-669">
                            {item.weeks}
                          </span>
                          <h3 className="font-semibold text-cream mb-1" data-id="element-670">
                            {item.title}
                          </h3>
                          <p className="text-sm text-[#b8b4ae]" data-id="element-671">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInSection>)}
            </div>
          </div>
        </div>
      </section>

      {/* MÉTRICAS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-672">
        <div className="max-w-5xl mx-auto" data-id="element-673">
          <FadeInSection data-id="element-674">
            <div className="text-center mb-12" data-id="element-675">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-676">
                Las métricas que importan
              </h2>
              <p className="text-[#b8b4ae] max-w-xl mx-auto text-sm" data-id="element-677">
                Estas métricas varían según mercado, producto y ejecución. Las
                mostramos para que tengas visibilidad, no para hacer
                proyecciones.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-678">
            {metrics.map((m, i) => <FadeInSection key={m.label} delay={i * 0.08} data-id="element-679">
                <div className="bg-elevated border border-border-subtle rounded-xl p-6 text-center" data-id="element-680">
                  <span className="text-2xl font-black text-accent block mb-1" data-id="element-681">
                    {m.label}
                  </span>
                  <span className="text-xs font-semibold text-cream block mb-3" data-id="element-682">
                    {m.subtitle}
                  </span>
                  <p className="text-xs text-[#b8b4ae] leading-relaxed" data-id="element-683">
                    {m.description}
                  </p>
                </div>
              </FadeInSection>)}
          </div>
        </div>
      </section>

      {/* FRACTIONAL CMO */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-684">
        <div className="max-w-5xl mx-auto" data-id="element-685">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-686">
            <FadeInSection data-id="element-687">
              <div data-id="element-688">
                <span className="text-accent text-xs font-semibold uppercase tracking-widest mb-3 block" data-id="element-689">
                  Post-instalación
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-5 leading-tight" data-id="element-690">
                  Fractional CMO: acompañamiento estratégico continuo
                </h2>
                <p className="text-[#b8b4ae] leading-relaxed" data-id="element-691">
                  Después de los 90 días, no te dejamos solo. Nuestro modelo de
                  Fractional CMO te da acceso a estrategia de marketing continua
                  sin contratar un equipo interno.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-692">
              <div className="bg-surface border border-border-subtle rounded-2xl p-6" data-id="element-693">
                <h3 className="text-sm font-semibold text-cream mb-4 uppercase tracking-widest" data-id="element-694">
                  Qué incluye
                </h3>
                <ul className="space-y-3" data-id="element-695">
                  {['Revisiones mensuales de métricas', 'Ajustes de campaña según rendimiento', 'Soporte en decisiones de crecimiento', 'Acceso a nuevas herramientas y tácticas', 'Comunicación directa con el equipo estratégico'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-696">
                      <CheckCircleIcon size={16} className="text-accent flex-shrink-0 mt-0.5" data-id="element-697" />
                      <span className="text-sm text-[#b8b4ae]" data-id="element-698">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-699">
        <div className="max-w-3xl mx-auto" data-id="element-700">
          <FadeInSection data-id="element-701">
            <h2 className="text-2xl sm:text-3xl font-bold text-cream text-center mb-10" data-id="element-702">
              Preguntas frecuentes
            </h2>
          </FadeInSection>
          <div className="space-y-3" data-id="element-703">
            {faqs.map((faq, i) => <FadeInSection key={faq.q} delay={i * 0.06} data-id="element-704">
                <FAQItem q={faq.q} a={faq.a} data-id="element-705" />
              </FadeInSection>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-id="element-706">
        <div className="max-w-3xl mx-auto text-center" data-id="element-707">
          <FadeInSection data-id="element-708">
            <div className="bg-surface border border-accent/20 rounded-3xl p-10 sm:p-14" data-id="element-709">
              <span className="inline-block w-12 h-1 bg-accent rounded-full mb-6" data-id="element-710" />
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-4" data-id="element-711">
                ¿Lista tu agencia para operar con infraestructura propia?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed" data-id="element-712">
                Agenda un diagnóstico gratuito. Evaluamos tu caso y te decimos
                exactamente qué necesitas instalar.
              </p>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200 text-base" data-id="element-713">
                Agendar diagnóstico
                <ArrowRightIcon size={18} data-id="element-714" />
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}